/**
 * Communication step for the Schedule Interview wizard.
 * Dual-pane layout: Candidate (left) | Interviewer (right).
 */
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { Flex } from '@chakra-ui/react';
import type { StepFormApi } from '@/hooks/useStepValidation';
import { useUploadUserInlineImage } from '@/api/userImageUploads';
import { useInterviewEmailTemplateOptions } from '../../../InterviewActivity/communication/api';
import { useInterviewActivityCommunicationVariables } from '../../../InterviewActivity/communication/api';
import {
  initialInterviewCommunicationFormState,
  interviewCommunicationReducer,
} from '../../../InterviewActivity/communication/communication.state';
import {
  hydrateCommunicationStateFromPayload,
  toInterviewCommunicationPayload,
} from '../../../InterviewActivity/communication/helpers/payloadMap';
import {
  hasValidationErrors,
  validateInterviewCommunicationForm,
  type CommunicationValidationVariables,
  type InterviewCommunicationValidationErrors,
} from '../../../InterviewActivity/communication/communication.validate';
import {
  getFirstScheduleCommunicationScrollTarget,
  scrollScheduleCommunicationSectionIntoView,
} from './scheduleCommunicationScroll';
import { calendarInviteTemplateNameForApi } from '../../../InterviewActivity/communication/helpers/calendarInviteName';
import { compositionFromTemplateOption } from '../../../InterviewActivity/communication/helpers/templateApply';
import { useScheduleSenderOptions } from '../../hooks/useScheduleSenderOptions';
import type { ScheduleStepProps } from '../../schedule.types';
import { CandidatePane } from './CandidatePane';
import { InterviewerPane } from './InterviewerPane';

export const CommunicationStep = memo(
  forwardRef<StepFormApi, ScheduleStepProps>(
    (
      { payloadRef, mode, apiReference: _apiReference, prefillVersion = 0 },
      ref
    ) => {
      // ---- Reducer state ----
      const [state, dispatch] = useReducer(
        interviewCommunicationReducer,
        initialInterviewCommunicationFormState,
        (base) =>
          hydrateCommunicationStateFromPayload(
            Object.keys(payloadRef.current.communication).length > 0
              ? (payloadRef.current.communication as Partial<
                  Record<string, unknown>
                >)
              : undefined,
            base,
            { stripCalendarTemplates: prefillVersion > 0 }
          )
      );

      const validateCommOptions = useMemo(
        () => ({
          validateSelfSchedule:
            mode === 'self-schedule' || mode === 'self-schedule-adhoc',
        }),
        [mode]
      );

      useEffect(() => {
        if (validateCommOptions.validateSelfSchedule) {
          return;
        }
        dispatch({ type: 'SET_SELF_SCHEDULE_ENABLED', payload: false });
      }, [dispatch, validateCommOptions.validateSelfSchedule]);

      const [fieldErrors, setFieldErrors] =
        useState<InterviewCommunicationValidationErrors>({});

      // ---- Data fetching ----
      const { data: candidateTemplateOptions = [] } =
        useInterviewEmailTemplateOptions('candidate');
      const { data: interviewerTemplateOptions = [] } =
        useInterviewEmailTemplateOptions('interviewer');
      const { fromOptions, ccBccOptions } = useScheduleSenderOptions();

      const {
        candidateCalendar: candidateCalendarVariables,
        interviewerCalendar: interviewerCalendarVariables,
        candidateEmail: candidateEmailVariables,
        interviewerEmail: interviewerEmailVariables,
      } = useInterviewActivityCommunicationVariables();

      const { mutateAsync: uploadUserInlineImageAsync } =
        useUploadUserInlineImage();
      const uploadInlineImage = useCallback(
        (file: File) => uploadUserInlineImageAsync(file),
        [uploadUserInlineImageAsync]
      );

      // ---- Validation variables ----
      const validationVariables: CommunicationValidationVariables = useMemo(
        () => ({
          candidateCalendar: candidateCalendarVariables,
          interviewerCalendar: interviewerCalendarVariables,
          candidateEmail: candidateEmailVariables,
          interviewerEmail: interviewerEmailVariables,
        }),
        [
          candidateCalendarVariables,
          candidateEmailVariables,
          interviewerCalendarVariables,
          interviewerEmailVariables,
        ]
      );

      // ---- Live validation after first submit failure ----
      useEffect(() => {
        setFieldErrors((prev) => {
          if (!hasValidationErrors(prev)) return prev;
          return validateInterviewCommunicationForm(
            state,
            validationVariables,
            validateCommOptions
          );
        });
      }, [state, validationVariables, validateCommOptions]);

      // ---- One-shot template defaults (create mode only) ----
      const didDefaultCandidateCal = useRef(false);
      const didDefaultInterviewerCal = useRef(false);

      useEffect(() => {
        if (prefillVersion > 0 || !candidateTemplateOptions.length) return;
        if (state.candidateCalendar.template) {
          didDefaultCandidateCal.current = true;
          return;
        }
        if (didDefaultCandidateCal.current) return;
        didDefaultCandidateCal.current = true;
        dispatch({
          type: 'SET_CANDIDATE_CALENDAR',
          payload: compositionFromTemplateOption(
            state.candidateCalendar,
            candidateTemplateOptions[0]
          ),
        });
      }, [prefillVersion, candidateTemplateOptions, state.candidateCalendar]);

      useEffect(() => {
        if (prefillVersion > 0 || !interviewerTemplateOptions.length) return;
        if (state.interviewerCalendar.template) {
          didDefaultInterviewerCal.current = true;
          return;
        }
        if (didDefaultInterviewerCal.current) return;
        didDefaultInterviewerCal.current = true;
        dispatch({
          type: 'SET_INTERVIEWER_CALENDAR',
          payload: compositionFromTemplateOption(
            state.interviewerCalendar,
            interviewerTemplateOptions[0]
          ),
        });
      }, [
        prefillVersion,
        interviewerTemplateOptions,
        state.interviewerCalendar,
      ]);

      // ---- Imperative handle ----
      useImperativeHandle(
        ref,
        () => ({
          validate: async () => {
            const errors = validateInterviewCommunicationForm(
              state,
              validationVariables,
              validateCommOptions
            );
            setFieldErrors(errors);
            const ok = !hasValidationErrors(errors);
            if (!ok) {
              const target = getFirstScheduleCommunicationScrollTarget(errors);
              if (target) {
                scrollScheduleCommunicationSectionIntoView(target);
              }
            }
            return ok;
          },
          syncToPayload: () => {
            const comm = toInterviewCommunicationPayload(state);
            comm._ui_calendar_candidate_name = calendarInviteTemplateNameForApi(
              state.candidateCalendar,
              candidateTemplateOptions
            );
            comm._ui_calendar_interviewer_name =
              calendarInviteTemplateNameForApi(
                state.interviewerCalendar,
                interviewerTemplateOptions
              );
            payloadRef.current.communication = comm as unknown as Record<
              string,
              unknown
            >;
          },
        }),
        [
          candidateTemplateOptions,
          interviewerTemplateOptions,
          payloadRef,
          state,
          validationVariables,
          validateCommOptions,
        ]
      );

      // ---- Render ----
      return (
        <Flex
          key={String(prefillVersion)}
          w="100%"
          h="100%"
          minH={0}
          flex="1"
          gap="6"
          overflow="auto"
          pb="8"
        >
          <CandidatePane
            state={state}
            dispatch={dispatch}
            fieldErrors={fieldErrors}
            templateOptions={candidateTemplateOptions}
            fromOptions={fromOptions}
            ccBccOptions={ccBccOptions}
            calendarVariables={candidateCalendarVariables}
            emailVariables={candidateEmailVariables}
            uploadInlineImage={uploadInlineImage}
          />
          <InterviewerPane
            state={state}
            dispatch={dispatch}
            fieldErrors={fieldErrors}
            templateOptions={interviewerTemplateOptions}
            fromOptions={fromOptions}
            ccBccOptions={ccBccOptions}
            calendarVariables={interviewerCalendarVariables}
            emailVariables={interviewerEmailVariables}
            uploadInlineImage={uploadInlineImage}
          />
        </Flex>
      );
    }
  )
);

CommunicationStep.displayName = 'CommunicationStep';
